import { Box, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { capitalizeFirstLetter, formatArrAmount, formatNextMeeting, NumberFilterValue, validateDate } from "../../utils/utils";
import ColumnFilter from "../Common/customFilter";
import { DateRangeValue } from "../../utils/types";
import EllipsedTooltip from "../Common/EllipsedTooltip";

export type OpportunityFilterOption = { label: string; value: string };

export interface OpportunityTableFilters {
  opportunityOptions?: OpportunityFilterOption[];
  accountOptions?: OpportunityFilterOption[];
  opportunityOwnerOptions?: OpportunityFilterOption[];
  dealStageOptions?: OpportunityFilterOption[];
  managerForecastOptions?: OpportunityFilterOption[];
  columnFilters?: {
    opportunityName?: string[];
    account?: string[];
    opportunityOwner?: string[];
    dealStage?: string[];
    managerForecast?: string[];
    nextMeeting?: DateRangeValue;
    arrAmount?: NumberFilterValue;
    closeDate?: DateRangeValue;
  };
  updateColumnFilter?: (key: string, value: string[] | DateRangeValue | NumberFilterValue | undefined) => void;
  openFilterColumn?: string | null;
  setOpenFilterColumn?: (key: string | null) => void;
  hideAccountFilter?: boolean;
  isAccountSlideOver?: boolean;
}

const cellTextSx = {
  fontSize: "13px",
  color: "#43434D",
  lineHeight: "18px",
};

const filterHeaderSx = (isOpen: boolean) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  backgroundColor: isOpen ? "#F2F2F2" : "inherit",
  borderRadius: "4px",
});

const headerTitleSx = {
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "18px",
};

const plainHeader = (title: string) => () => (
  <Box sx={filterHeaderSx(false)}>
    <Typography sx={headerTitleSx}>{title}</Typography>
  </Box>
);

const filterHeader = (
  title: string,
  columnKey: "opportunityName" | "account" | "opportunityOwner" | "dealStage" | "managerForecast",
  options: OpportunityFilterOption[],
  filters?: OpportunityTableFilters,
  hideUntilSearchThreshold?: number,
) => {
  if (!filters?.updateColumnFilter) return undefined;
  const currentValue = filters.columnFilters?.[columnKey] ?? [];
  const isActive = currentValue.length > 0;
  return () => (
    <Box sx={filterHeaderSx(filters.openFilterColumn === columnKey)}>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "14px",
          lineHeight: "18px",
          color: isActive ? "#6036d6" : "inherit",
        }}
      >
        {title}
      </Typography>
      <Box onClick={(event) => event.stopPropagation()} sx={{ display: "inline-flex" }}>
      <ColumnFilter
        currentValue={currentValue}
        onApply={(val) => {
          const next = Array.isArray(val) ? val.map(String) : [];
          filters.updateColumnFilter?.(columnKey, next);
        }}
        options={options}
        type="multi"
        showSections={{ search: true, sort: false }}
        columnKey={columnKey}
        parent_page={columnKey === "account" ? "opportunities-page" : undefined}
        hideUntilSearchThreshold={hideUntilSearchThreshold}
        onOpen={() => filters.setOpenFilterColumn?.(columnKey)}
        onClose={() => filters.setOpenFilterColumn?.(null)}
        isActive={isActive}
      />
      </Box>
    </Box>
  );
};

const dateFilterHeader = (
  title: string,
  columnKey: "nextMeeting" | "closeDate",
  filters?: OpportunityTableFilters,
) => {
  if (!filters?.updateColumnFilter) return undefined;
  const currentValue = filters.columnFilters?.[columnKey];
  const isActive = Boolean(currentValue?.startDate && currentValue?.endDate);
  return () => (
    <Box sx={filterHeaderSx(filters.openFilterColumn === columnKey)}>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "14px",
          lineHeight: "18px",
          color: isActive ? "#6036d6" : "inherit",
        }}
      >
        {title}
      </Typography>
      <Box onClick={(event) => event.stopPropagation()} sx={{ display: "inline-flex" }}>
        <ColumnFilter
          currentValue={currentValue}
          onApply={(val) => {
            if (
              val &&
              typeof val === "object" &&
              !Array.isArray(val) &&
              "startDate" in val &&
              "endDate" in val
            ) {
              filters.updateColumnFilter?.(columnKey, val as DateRangeValue);
              return;
            }
            filters.updateColumnFilter?.(columnKey, undefined);
          }}
          options={[]}
          type="date"
          showSections={{ search: false, sort: false, options: false, apply: true }}
          columnKey={columnKey}
          onOpen={() => filters.setOpenFilterColumn?.(columnKey)}
          onClose={() => filters.setOpenFilterColumn?.(null)}
          isActive={isActive}
        />
      </Box>
    </Box>
  );
};

const numberFilterHeader = (
  title: string,
  columnKey: "arrAmount",
  filters?: OpportunityTableFilters,
) => {
  if (!filters?.updateColumnFilter) return undefined;
  const currentValue = filters.columnFilters?.arrAmount;
  const isActive = currentValue?.value !== undefined && Boolean(currentValue?.operator);
  return () => (
    <Box sx={filterHeaderSx(filters.openFilterColumn === columnKey)}>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "14px",
          lineHeight: "18px",
          color: isActive ? "#6036d6" : "inherit",
        }}
      >
        {title}
      </Typography>
      <Box onClick={(event) => event.stopPropagation()} sx={{ display: "inline-flex" }}>
        <ColumnFilter
          currentValue={currentValue}
          onApply={(val) => {
            if (
              val &&
              typeof val === "object" &&
              !Array.isArray(val) &&
              "operator" in val &&
              "value" in val
            ) {
              filters.updateColumnFilter?.(columnKey, val as NumberFilterValue);
              return;
            }
            filters.updateColumnFilter?.(columnKey, undefined);
          }}
          options={[]}
          type="number"
          showSections={{ search: false, sort: false, options: false, apply: true }}
          columnKey={columnKey}
          numberPlaceholder="Insert ARR"
          onOpen={() => filters.setOpenFilterColumn?.(columnKey)}
          onClose={() => filters.setOpenFilterColumn?.(null)}
          isActive={isActive}
        />
      </Box>
    </Box>
  );
};

const getOpportunitiesColumns = (
  filters?: OpportunityTableFilters,
  onOpportunityClick?: (opportunity: any) => void
): MRT_ColumnDef<any>[] => {

  return [
    {
      accessorKey: "opportunityName",
      header: "Opportunity Name",
      Header: filterHeader(
        "Opportunity Name",
        "opportunityName",
        filters?.opportunityOptions ?? [],
        filters,
        5,
      ),
      enableSorting: true,
      size: 300,
      minSize: 280,
      Cell: ({ row }: { row: any }) => {
        const name = row.original?.opportunityName || "";
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              padding: "4px 0px",
              cursor: onOpportunityClick ? "pointer" : "default",
            }}
            onClick={() => onOpportunityClick?.(row.original)}
          >
            <EllipsedTooltip tooltipContent={name} maxWidth="300px">
              <span
                style={{
                  ...cellTextSx,
                  color: onOpportunityClick ? "#1976d2" : "#43434D",
                  fontWeight: 500,
                  textDecoration: onOpportunityClick ? "underline" : "none",
                }}
                className="truncate-table-cell-ellipsis"
              >
                {name || "---"}
              </span>
            </EllipsedTooltip>
          </Box>
        );
      },
    },
    {
      accessorKey: "account",
      header: "Account",
      Header: filters?.hideAccountFilter
        ? undefined
        : filterHeader(
            "Account",
            "account",
            filters?.accountOptions ?? [],
            filters,
          ),
      enableSorting: false,
      size: 150,
      minSize: 130,
      Cell: ({ row }: { row: any }) => (
        <Box sx={{ ...cellTextSx, padding: "4px 0px" }}>
          {capitalizeFirstLetter(row.original?.account || "---")}
        </Box>
      ),
    },
    {
      accessorKey: "arrAmount",
      header: "ARR Amount",
      Header: numberFilterHeader("ARR Amount", "arrAmount", filters),
      enableSorting: true,
      size: 160,
      minSize: 120,
      Cell: ({ row }: { row: any }) => {
        const raw = row.original?.arrAmount;
        return (
          <Box sx={{ ...cellTextSx, padding: "4px 0px" }}>
            {raw === null || raw === undefined || raw === "" ? "---" : formatArrAmount(raw)}
          </Box>
        );
      },
    },
    {
      accessorKey: "opportunityOwner",
      header: "Opp. Owner",
      Header: filterHeader(
        "Opp. Owner",
        "opportunityOwner",
        filters?.opportunityOwnerOptions ?? [],
        filters,
      ),
      enableSorting: false,
      size: 180,
      minSize: 130,
      Cell: ({ row }: { row: any }) => (
        <Box sx={{ ...cellTextSx, padding: "4px 0px" }}>
          {row.original?.opportunityOwner || "---"}
        </Box>
      ),
    },
    {
      accessorKey: "dealStage",
      header: "Deal Stage",
      Header: filterHeader(
        "Deal Stage",
        "dealStage",
        filters?.dealStageOptions ?? [],
        filters,
      ),
      enableSorting: false,
      size: 150,
      minSize: 130,
      Cell: ({ row }: { row: any }) => (
        <Box sx={{ ...cellTextSx, padding: "4px 0px" }}>
          {row.original?.dealStage || "---"}
        </Box>
      ),
    },
    {
      accessorKey: "closeDate",
      header: "Closed Date",
      Header: filters?.isAccountSlideOver
        ? dateFilterHeader("Closed Date", "closeDate", filters)
        : plainHeader("Closed Date"),
      enableSorting: true,
      size: filters?.isAccountSlideOver ? 160 : 140,
      minSize: filters?.isAccountSlideOver ? 150 : 130,
      Cell: ({ row }: { row: any }) => (
        <Box sx={{ ...cellTextSx, padding: "4px 0px" }}>
          {validateDate(row.original?.closeDate, "MMM D, YYYY")}
        </Box>
      ),
    },
    {
      accessorKey: "nextMeeting",
      header: "Next Meeting",
      Header: dateFilterHeader("Next Meeting", "nextMeeting", filters),
      enableSorting: true,
      size: 160,
      minSize: 140,
      Cell: ({ row }: { row: any }) => {
        const meeting = formatNextMeeting(row.original?.nextMeeting);
        if (!meeting) {
          return <Box sx={{ ...cellTextSx, padding: "4px 0px" }}>---</Box>;
        }
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", padding: "4px 0px" }}>
            <Box sx={cellTextSx}>{meeting || "---"}</Box>
          </Box>
        );
      },
    },
    {
      accessorKey: "managerForecast",
      header: "Manager Forecast",
      Header: filterHeader(
        "Manager Forecast",
        "managerForecast",
        filters?.managerForecastOptions ?? [],
        filters,
      ),
      enableSorting: false,
      size: 160,
      minSize: 140,
      Cell: ({ row }: { row: any }) => {
        const forecast = row.original?.managerForecast;
        if (!forecast) return <Box sx={{ ...cellTextSx, padding: "4px 0px" }}>---</Box>;
        return (
          <Box sx={{ padding: "4px 0px" }}>
            {forecast}
          </Box>
        );
      },
    },
  ];
};

export default getOpportunitiesColumns;
